/**
 * 把 hash 當成唯一的「目前在哪一頁」來源（#01）。
 *
 * - 初次 render 就從 `location.hash` 讀，所以 F5／直接貼網址都落在同一頁；
 * - 監聽 `hashchange`（手改網址、`location.hash=`）與 `popstate`（上一頁／下一頁），
 *   所以按瀏覽器上一頁是回面板的前一頁，不是離站；
 * - `navigate` 預設 push（分區切換要能回上一頁），`{ replace: true }` 用於
 *   「同一個視圖內的細節改變」（例如在收件匣點開另一條對話），不要塞爆歷史。
 *
 * 這一層**不碰身分／登入**：PIN 守門在 `auth/AuthGate`，它在 `<App />` 之外，
 * 未登入時 App 根本不會掛載，所以亂打 `#/admin/...` 也不會繞過閘門。
 */
import { useCallback, useEffect, useState } from 'react';
import { parseRoute, routeToUrl, type Route } from '../shell/routes';

export interface NavigateOptions {
  replace?: boolean;
}

export interface UseHashRouteResult {
  route: Route;
  navigate: (next: Route, options?: NavigateOptions) => void;
}

export function useHashRoute(): UseHashRouteResult {
  const [route, setRoute] = useState<Route>(() =>
    parseRoute(typeof window === 'undefined' ? '' : window.location.hash),
  );

  useEffect(() => {
    const sync = () => {
      const next = parseRoute(window.location.hash);
      // 舊書籤／亂打的網址收斂成正式形式（`#/health` → `#/admin/sessions`），
      // 但只在原本就有 hash 時動，載入首頁不要多塞一段進網址列。
      const url = routeToUrl(next);
      if (window.location.hash && window.location.hash !== url) {
        window.history.replaceState(window.history.state, '', url);
      }
      setRoute(next);
    };
    window.addEventListener('hashchange', sync);
    window.addEventListener('popstate', sync);
    // 掛載時網址可能已被外部改過（或有 `#/health` 這種舊形式），先對齊一次。
    sync();
    return () => {
      window.removeEventListener('hashchange', sync);
      window.removeEventListener('popstate', sync);
    };
  }, []);

  const navigate = useCallback((next: Route, options?: NavigateOptions) => {
    const url = routeToUrl(next);
    if (window.location.hash !== url) {
      if (options?.replace) window.history.replaceState(window.history.state, '', url);
      else window.history.pushState(window.history.state, '', url);
    }
    setRoute(next);
  }, []);

  return { route, navigate };
}
